import { Observable, of } from "rxjs";
import { DocMetadata, DocMetadataDto, DocMetadataGroup, DocMetadataInfo, EntityType, ValueDefinition } from "../model/doc-metadata";
import { Injectable } from "@angular/core";
import { Config } from "src/app/config";
import { HttpClient } from "@angular/common/http";
import { AuthService } from "@shared/services/auth.service";
import { catchError, map } from "rxjs/operators";
import { Doc, newCatSystemVersion, ServiceCallInfo } from "@shared/models/common";
import { InfoItem } from "@shared/components/info-drawer/info-drawer.component";
import { CatDoc } from "@shared/models/cat.model";
import { RevDoc } from "@shared/models/rev.model";

@Injectable()
export class DocMetadataHandler {

    private nextId = 1;

    docMetadataByGroup: {[group: string]: DocMetadata[]} = {};
    groups: DocMetadataGroup[] = [];
    selections: {[group: string]: DocMetadata[] | DocMetadata | null} = {};
    docMetadataToAdd: DocMetadata[] = [];
    scaiServiceUrl: string;
    newCatSystemVersion = newCatSystemVersion;

    constructor(private http: HttpClient,
                private authService: AuthService,
    ) {
            this.scaiServiceUrl = Config.assetConfigData['scaiServiceUrl'];
        
    }
    mockDocMetadataInfo(): Observable<DocMetadataInfo> {

        /*

        [
    {
        "field_label": "Brand Name",
        "field_name": "brand_name",
        "field_type": "text",
        "is_multivalued": false
    },
    {
        "field_label": "Product",
        "field_name": "product",
        "field_type": "text",
        "is_multivalued": true
    }
]
    */

        let docMetadataDto: DocMetadataDto[] = [
            {
                "field_name": "brand_name",
                "field_label": "Brand name",
                "is_multivalued": false,
                "is_required": true,
                "lov": [
                    {
                        "value_code": "1",
                        "value_label": "One"
                    },
                    {
                        "value_code": "2",
                        "value_label": "Two"
                    }
                ],
                "default_value": null
            },
            {
                "field_name": "product_name",
                "field_label": "Product",
                "is_multivalued": true,
                "is_required": false,
                "lov": [
                    {
                        "value_code": "1",
                        "value_label": "One"
                    },
                    {
                        "value_code": "2",
                        "value_label": "Two"
                    }
                ],
                "default_value": null
            }
        ]

        const docMetadataInfo: DocMetadataInfo = this.extractDocMetadataInfo(docMetadataDto);
        this.docMetadataByGroup = docMetadataInfo?.docMetadataByGroup;
        this.groups = docMetadataInfo?.groups;

        return of(docMetadataInfo);
    }


    getDocMetadataDefinition(envId: number, catVersionId:number, lang: string, assetType: string | null, entityType: EntityType): Observable<DocMetadataInfo> {

        let url = `${this.scaiServiceUrl}metadata/field-definitions?env_id=${envId}&cat_version_id=${catVersionId}&lang=${lang}`;
        if (assetType) {
            url = url.concat(`&asset_type=${assetType}`);
        }
        if (entityType) {
            url = url.concat(`&entity_type=${entityType}`);
        }

        this.docMetadataToAdd = [];
        this.docMetadataByGroup = {};
        this.selections = {};
        this.groups = [];

        return this.http.get<DocMetadataDto[]>(url, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json',
      }).pipe(map((data: DocMetadataDto[]) => {

        const docMetadataInfo = this.extractDocMetadataInfo(data);
        this.docMetadataByGroup = docMetadataInfo?.docMetadataByGroup;
        this.groups = docMetadataInfo?.groups;
        return docMetadataInfo;
         
      }),
        catchError((err) => {

            const info = new ServiceCallInfo(url, url, "");
            err.message = err.error.description || err.statusText;
            return this.authService.handleError(err, info

             )}))
        

    }

    private mapListOfValuesToDocMetadata(data: DocMetadataDto): DocMetadata[] {

        const listOfValues: ValueDefinition[] =  data.lov;
        const mappedMetadata: DocMetadata[] = [];

        listOfValues.forEach((lov) => {
            
            mappedMetadata.push(new DocMetadata("", lov.value_label, lov.value_code, this.nextId++));

        })

        return mappedMetadata;

    }
    protected initializeGroupSelections(data: DocMetadataDto, docMetadataList: DocMetadata[]): boolean {

        this.selections[data.field_name] = data.is_multivalued ? [] : null;
        if (data.default_value != null) {

            let preSelectedMetadata = docMetadataList.find((groupMetadata) => groupMetadata.fieldValue == data.default_value);
            if (preSelectedMetadata) {
                
                this.selections[data.field_name] = !this.selections[data.field_name] ? preSelectedMetadata : [preSelectedMetadata];
            }
            return true;
        }
        return false;
    }
     protected extractDocMetadataInfo(data: DocMetadataDto[]): DocMetadataInfo {

        const groups: DocMetadataGroup[] = [];
        const docMetadataByGroup: {[group: string]: DocMetadata[]} = {};
        //each element of doc metadata dto is acutally a one group of metadata, so we need to map each group to a list of doc metadata and also create a list of groups
        data.forEach((docMetadataDto: DocMetadataDto) => {


            docMetadataByGroup[docMetadataDto.field_name] = this.mapListOfValuesToDocMetadata(docMetadataDto);
            const hasDefaultValue = this.initializeGroupSelections(docMetadataDto, docMetadataByGroup[docMetadataDto.field_name]);
            const group: DocMetadataGroup = {
               groupName: docMetadataDto.field_name,
                isMultiValued: docMetadataDto.is_multivalued ?? false,
                groupLabel: docMetadataDto.field_label,
                hasDefaultValue: hasDefaultValue,
                isRequired: docMetadataDto.is_required
            };
            groups.push(group);
        });
        // this info is required in component to display the groups and also to get the doc metadata by group name
        return {
            groups: groups,
            docMetadataByGroup: docMetadataByGroup
        }
    }

    handleDocMetadata(docMetadata: DocMetadata) {

        
        let foundIndex = this.docMetadataToAdd.findIndex(metadata => metadata.clientId == docMetadata.clientId);
                    
        if (docMetadata.fieldValue == '') {

            if (foundIndex != -1) {
                this.docMetadataToAdd.splice(foundIndex, 1);
            }
        } else {
            if (foundIndex == -1) {
                this.docMetadataToAdd.push(docMetadata);
            }
        }
        

    }
    addDocMetadataToGroup(groupName: string) {

        let docMetadata = this.docMetadataByGroup[groupName];
        docMetadata.push(new DocMetadata(docMetadata[0].fieldName, docMetadata[0].fieldLabel, "", this.nextId++))
    }

    removeDocMetadataFromGroup(groupName: string, docMetadata: DocMetadata) {

        let groupDocMetadata = this.docMetadataByGroup[groupName];
        const foundIndex = groupDocMetadata.findIndex((metadata) => metadata.clientId == docMetadata.clientId);
        if (foundIndex != -1) {

            groupDocMetadata.splice(foundIndex, 1);
        }
    }

    getDocMetadataToAdd(): { [key: string]: string[] } | null {

        let groups = Object.keys(this.selections);

        if (!groups || groups.length == 0) {

            return null;
        }

        let metadata: { [key: string]: string[] } | null = null;
        for (let group of groups) {

            let docMetadataValues = this.selections[group];

            if (docMetadataValues != null) {
                
                if (!Array.isArray(docMetadataValues) || (Array.isArray(docMetadataValues) && docMetadataValues.length > 0)) {
                    
                    if (metadata == null) {
                        metadata = {};
                    }
                    let metadataValues: string[] = [];
                    if (!Array.isArray(docMetadataValues)) {

                        metadataValues.push(docMetadataValues.fieldValue);
                    } else {
                        metadataValues = docMetadataValues.map(metadata => metadata.fieldValue);
                    }
                    metadata[group] = metadataValues;
                }

            }

            
        }
        return metadata;
    }
    transformDocMetadataDtoToInfoItem(docMetadata: DocMetadataDto): InfoItem {
        return {
            label: docMetadata.field_label,
            value: docMetadata.lov.map((v) => v.value_label).join(', ') // concat values with comma if multiple values exist
        }
    }
    getDocMetadata(docId: number, lang: string, isPrecheck: boolean = true): Observable<DocMetadataDto[]> {
        const url = this.scaiServiceUrl + `metadata/document?doc_id=${docId}&is_precheck=${isPrecheck}&lang=${lang}`;
        return this.http.get<DocMetadataDto[]> (url, {
            headers: this.authService.getRequestHeaders(),
            withCredentials: false,
            responseType: 'json'
        });
    }
    saveDocMetadata(docId: number, metadata: { [key: string]: string[] }, isPrecheck: boolean) {
        const url = this.scaiServiceUrl + `documents/${docId}/metadata`;
        return this.http.put(url, { 
            metadata: metadata,
            is_precheck: isPrecheck 
        }, {
            headers: this.authService.getRequestHeaders(),
            withCredentials: false,
            responseType: 'json'
        }).pipe(catchError((error) => {
            return this.authService.handleError(error, new ServiceCallInfo('update_document_metadata', '', url));
        }));
    }
    public getBasicDocMetadata(doc: Doc, isPrecheck: boolean = true): InfoItem[] {
        if (isPrecheck) {
            return this.getBasicRevDocMetadata(doc as RevDoc);
        } else {
            return this.getBasicCatDocMetadata(doc as CatDoc);
        }

    }
    private getBasicRevDocMetadata(doc: RevDoc): InfoItem[] {

        const docMetadata: InfoItem[] = [];
        
            docMetadata.push({label: 'Document ID', value: doc.docId});
            docMetadata.push({label: 'Created date', value: doc.createDateFormatted});
            docMetadata.push({label: 'Library title', value: doc.catDescr});
/*             if (doc.newCatVersionSw) {
                docMetadata.push({label: 'Library version', value: doc.catVersionId });
            } */
            docMetadata.push({label: 'Version', value: doc.versionId});
            docMetadata.push({label: 'File name', value: doc.fileName});
            docMetadata.push({label: 'Uploaded by', value: doc.uploadUserDescr});
            docMetadata.push({label: 'Library version ID', value: doc.actCatVersionId});
            if (doc.assetType) {
                docMetadata.push({label: "Asset type", value: doc.assetType.text});
            }
            if (!this.newCatSystemVersion(doc.catSystemVersion)) {
                docMetadata.push({label: "Product", value: doc.productDescr});
            }

        return docMetadata;
        
    }

    private getBasicCatDocMetadata(doc: CatDoc): InfoItem[] {

        const docMetadata: InfoItem[] = [];

        docMetadata.push({label: 'ID', value: doc.docId});
        docMetadata.push({label: 'Document name', value: doc.docName});
        docMetadata.push({label: 'Version', value: doc.versionId});
        docMetadata.push({label: 'File name', value: doc.fileName});
        if (doc.assetType) {
            docMetadata.push({label: 'Asset type', value: doc.assetType.text});
        }
        docMetadata.push({label: 'Created date', value: doc.createDate});
        docMetadata.push({label: 'Status', value: doc.statusDescr});
        return docMetadata;
    }
    isFormControlDisabled(group: DocMetadataGroup) {

        return group.hasDefaultValue;
    }
}
